import { Link } from '@inertiajs/react'
import PlatformLayout from '@/components/PlatformLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import {
  Building2,
  Users,
  GraduationCap,
  Calendar,
  DollarSign,
  ArrowRight,
  TrendingUp,
  Wallet,
} from 'lucide-react'

interface RecentSchool {
  id: number
  name: string
  slug: string | null
  active: boolean
  students_count: number
  teachers_count: number
  created_at: string
}

interface TopSchool {
  id: number
  name: string
  slug: string | null
  active: boolean
  students_count: number
  revenue: number
}

interface MonthPoint {
  label: string
  new_schools: number
  revenue: number
}

interface Stats {
  schools_total: number
  schools_active: number
  schools_inactive: number
  schools_new_this_month: number
  students_total: number
  teachers_total: number
  lessons_total: number
  lessons_this_month: number
  revenue_total: number
  revenue_this_month: number
}

interface Payouts {
  awaiting_payment_amount: number
  awaiting_payment_count: number
  not_closed_amount: number
  not_closed_count: number
  margin_amount: number
  next_payout_date: string
}

interface Props {
  stats: Stats
  payouts: Payouts
  monthly_series: MonthPoint[]
  top_schools: TopSchool[]
  recent_schools: RecentSchool[]
}

const fmtBRL = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtBRLShort = (n: number) => {
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1).replace('.', ',')}k`
  return fmtBRL(n)
}

export default function Dashboard({
  stats,
  payouts,
  monthly_series,
  top_schools,
  recent_schools,
}: Props) {
  const cards = [
    {
      label: 'Escolas',
      value: stats.schools_total,
      hint: `${stats.schools_active} ativas · ${stats.schools_inactive} desativadas`,
      icon: Building2,
      accent: 'text-sky-600',
    },
    {
      label: 'Alunos',
      value: stats.students_total.toLocaleString('pt-BR'),
      hint: 'em todas as escolas',
      icon: Users,
      accent: 'text-violet-600',
    },
    {
      label: 'Professores',
      value: stats.teachers_total.toLocaleString('pt-BR'),
      hint: 'em todas as escolas',
      icon: GraduationCap,
      accent: 'text-amber-600',
    },
    {
      label: 'Aulas',
      value: stats.lessons_total.toLocaleString('pt-BR'),
      hint: `${stats.lessons_this_month.toLocaleString('pt-BR')} este mês`,
      icon: Calendar,
      accent: 'text-cyan-600',
    },
    {
      label: 'Receita total',
      value: fmtBRL(stats.revenue_total),
      hint: `${fmtBRL(stats.revenue_this_month)} este mês`,
      icon: DollarSign,
      accent: 'text-emerald-600',
    },
  ]

  const maxRevenue = Math.max(1, ...top_schools.map((s) => s.revenue))

  return (
    <PlatformLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Visão geral</h1>
            <p className="text-sm text-muted-foreground">
              Métricas agregadas de toda a plataforma Bee Wave
            </p>
          </div>
          <Link href="/platform/schools/new">
            <Button>Cadastrar escola</Button>
          </Link>
        </div>

        {/* Repasse é obrigação da BeWave com as escolas — fica acima das
            métricas de crescimento porque tem data pra sair. */}
        <Card className="border-emerald-200 dark:border-emerald-900">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-emerald-600" />
              <CardTitle className="text-base">Repasses às escolas</CardTitle>
            </div>
            <Link href="/platform/payouts">
              <Button variant="outline" size="sm">
                Gerenciar <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">
                  A pagar (fechado)
                </p>
                <p className="text-xl font-bold">
                  {fmtBRL(payouts.awaiting_payment_amount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {payouts.awaiting_payment_count} quinzena(s)
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Ainda não fechado
                </p>
                <p className="text-xl font-bold">
                  {fmtBRL(payouts.not_closed_amount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {payouts.not_closed_count} venda(s)
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Margem BeWave</p>
                <p
                  className={`text-xl font-bold ${
                    payouts.margin_amount < 0
                      ? 'text-red-600'
                      : 'text-emerald-600'
                  }`}
                >
                  {fmtBRL(payouts.margin_amount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  sobre o já liquidado
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Próximo pagamento
                </p>
                <p className="text-xl font-bold">
                  {new Date(
                    payouts.next_payout_date + 'T12:00:00',
                  ).toLocaleDateString('pt-BR')}
                </p>
                <p className="text-xs text-muted-foreground">
                  quinzena corrente
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {cards.map((c) => {
            const Icon = c.icon
            return (
              <Card key={c.label}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {c.label}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${c.accent}`} />
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{c.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.hint}</p>
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gráfico: crescimento */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Crescimento — últimos 6 meses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={monthly_series}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12 }}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 12 }}
                      stroke="hsl(var(--muted-foreground))"
                      allowDecimals={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }}
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(v) => fmtBRLShort(Number(v))}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === 'Receita'
                          ? fmtBRL(Number(value))
                          : (value as number)
                      }
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 8,
                        fontSize: 12,
                        color: 'hsl(var(--popover-foreground))',
                      }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="new_schools"
                      name="Novas escolas"
                      fill="#38bdf8"
                      radius={[4, 4, 0, 0]}
                      barSize={28}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      name="Receita"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top escolas por receita */}
          <Card>
            <CardHeader>
              <CardTitle>Top escolas por receita</CardTitle>
            </CardHeader>
            <CardContent>
              {top_schools.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Sem receita registrada ainda.
                </p>
              ) : (
                <div className="space-y-4">
                  {top_schools.map((s) => (
                    <Link
                      key={s.id}
                      href={`/platform/schools/${s.id}`}
                      className="block group"
                    >
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium group-hover:text-primary truncate flex items-center gap-2">
                          {s.name}
                          {!s.active && (
                            <span className="text-xs text-destructive">
                              (desativada)
                            </span>
                          )}
                        </span>
                        <span className="text-muted-foreground shrink-0 ml-2">
                          {fmtBRL(s.revenue)}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{
                            width: `${Math.max(4, (s.revenue / maxRevenue) * 100)}%`,
                          }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Escolas recentes</CardTitle>
            <Link
              href="/platform/schools"
              className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
            >
              Ver todas <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {recent_schools.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Nenhuma escola cadastrada ainda.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-2 font-medium">Escola</th>
                      <th className="py-2 font-medium">Status</th>
                      <th className="py-2 font-medium">Alunos</th>
                      <th className="py-2 font-medium">Professores</th>
                      <th className="py-2 font-medium">Cadastro</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_schools.map((s) => (
                      <tr
                        key={s.id}
                        className={`border-b last:border-0 ${s.active ? '' : 'opacity-60'}`}
                      >
                        <td className="py-3 font-medium">{s.name}</td>
                        <td className="py-3">
                          {s.active ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300">
                              Ativa
                            </Badge>
                          ) : (
                            <Badge variant="destructive">Desativada</Badge>
                          )}
                        </td>
                        <td className="py-3">{s.students_count}</td>
                        <td className="py-3">{s.teachers_count}</td>
                        <td className="py-3 text-muted-foreground">
                          {s.created_at}
                        </td>
                        <td className="py-3 text-right">
                          <Link
                            href={`/platform/schools/${s.id}`}
                            className="text-primary hover:underline"
                          >
                            ver
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  )
}
